import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { Button, Input } from '@column/column-ui-kit';

import { ROUTE } from '../routes';
import { NotificationList } from '~/components';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Headline } from '~/styles';
import { log, validateEmail } from '~/util';

import { HintLink, Logo, Panel } from './_components';

export const PageForgotPassword: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { control, getValues, handleSubmit } = useForm();

  const executeReCAPTCHA = useReCAPTCHA();

  const onSuccess = () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    executeReCAPTCHA(RecaptchaAction.PasswordResetRequest)
      .then((reCaptchaToken: string) => {
        UserRepository.createPasswordReset(getValues('email'), reCaptchaToken)
          .then(() => {
            setIsLoading(false);

            log({
              name: 'User password requested',
              context: {
                email: getValues('email'),
              },
            });

            addSuccessNotification({
              content: "We've sent an email with a link to reset your password.",
              display: 'page-login',
            });
          })
          .catch((error) => {
            setIsLoading(false);

            addDangerNotification({
              content: error.message,
              display: 'page-login',
            });
          });
      })
      .catch(() => setIsLoading(false));
  };

  return (
    <Panel>
      <Logo size="24px" />
      <Headline fullWidth size="small">
        Forgot password
      </Headline>
      <form onSubmit={handleSubmit(onSuccess)}>
        <NotificationList display="page-login" />
        <Flex flexDirection="column" gap={16} mb={24}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: true,
              validate: validateEmail,
            }}
            render={({ field, fieldState: { error, isTouched } }) => (
              <Input placeholder="Email address" hasError={isTouched && !!error} {...field} />
            )}
          />
        </Flex>
        <Flex flexDirection="column" gap={16}>
          <Button fullWidth isLoading={isLoading}>
            Request
          </Button>
          <HintLink as={Link} to={ROUTE.LOGIN}>
            Back to Login
          </HintLink>
        </Flex>
      </form>
    </Panel>
  );
};
